import type { MouseEvent } from 'react';

export function normalizeOpenInNewTab(value: unknown): boolean {
    const raw =
        value && typeof value === 'object' && 'value' in value
            ? (value as { value?: unknown }).value
            : value;
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'number') return raw !== 0;
    if (typeof raw === 'string') {
        const normalized = raw.trim().toLowerCase();
        return ['true', '1', 'on', 'yes'].includes(normalized);
    }
    return false;
}

function handleOpenInNewTabCapture(event: MouseEvent<HTMLAnchorElement>) {
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;

    const href = String(event.currentTarget?.href || '').trim();
    if (!href || href === '#') return;

    const targetWindow =
        window.parent && window.parent !== window ? window.parent : window;
    let opened: Window | null = null;
    try {
        opened = targetWindow.open(href, '_blank');
    } catch {
        opened = window.open(href, '_blank');
    }

    if (!opened) return;

    if (opened) {
        try {
            opened.opener = null;
        } catch {
            // Some browsers restrict opener mutation on cross-window proxies.
        }
    }

    event.preventDefault();
    event.stopPropagation();
    if (
        event.nativeEvent &&
        typeof event.nativeEvent.stopImmediatePropagation === 'function'
    ) {
        event.nativeEvent.stopImmediatePropagation();
    }
}

export function buttonLinkTargetProps(openInNewTab: unknown): {
    onClickCapture?: (event: MouseEvent<HTMLAnchorElement>) => void;
    target?: '_blank';
    rel?: string;
} {
    return normalizeOpenInNewTab(openInNewTab)
        ? {
              target: '_blank',
              rel: 'noopener noreferrer',
              onClickCapture: handleOpenInNewTabCapture,
          }
        : {};
}
